/**
 * Capture settings for the non-identity photos on a registration form.
 *
 * Identificação has always opened the live IP-camera capture (PhotoIPCameraModal) via
 * PhotoSection. Doc. Frente, Doc. Verso and Veículo instead opened AllPhotosEditModal — the
 * multi-column "Editar Fotos" grid — so the same gesture on neighbouring tiles produced two
 * different tools, and the one reachable for documents was the one that does not show a live
 * feed until you have picked a source from a dropdown.
 *
 * This is the per-type half of putting them on the same tool. The category values mirror
 * AllPhotosEditModal's getCamerasForColumn so the operator sees the same cameras either way.
 */

export type CapturePhotoType = 'document_front' | 'document_back' | 'vehicle';

export interface PhotoCaptureConfig {
  /** Header shown in the capture modal, and the tile's caption. */
  label: string;
  /**
   * Camera category to narrow the picker to, or undefined for "offer them all".
   *
   * Vehicles get undefined on purpose: AllPhotosEditModal applies no category filter to that
   * column either, and condo plate/entrance cameras are rarely tagged — narrowing here would
   * hand the operator an empty list on a form that has a vehicle tile.
   */
  filterCategory?: string;
  cropShape: 'round' | 'rect';
  aspectRatio: number;
  /** There is no face in a driving licence or a car; looking for one only slows the capture. */
  disableFaceDetection: boolean;
}

/** Landscape, matching the aspect-[3/2] tiles these captures fill. */
const RECT_ASPECT = 3 / 2;

const CONFIGS: Record<CapturePhotoType, PhotoCaptureConfig> = {
  document_front: {
    label: 'Doc. Frente',
    filterCategory: 'documento',
    cropShape: 'rect',
    aspectRatio: RECT_ASPECT,
    disableFaceDetection: true,
  },
  document_back: {
    label: 'Doc. Verso',
    filterCategory: 'documento',
    cropShape: 'rect',
    aspectRatio: RECT_ASPECT,
    disableFaceDetection: true,
  },
  vehicle: {
    label: 'Veículo',
    filterCategory: undefined,
    cropShape: 'rect',
    aspectRatio: RECT_ASPECT,
    disableFaceDetection: true,
  },
};

export const captureConfigForPhotoType = (type: CapturePhotoType): PhotoCaptureConfig => CONFIGS[type];
